/**
 * Targeting keys that exist only in the SPA form and must never reach the DSP when saving a line item.
 *
 * Shared by the Properties tab (`campaign-details.component.ts`) and the line item modal
 * (`line-item-edit-modal.component.ts`), which used to carry two identical inline copies.
 *
 * `video_positions` is deliberately NOT here: the DSP stores it on the line item and returns it in the
 * same `{ "pre-roll": bool, ... }` shape the form sends. Stripping it made the position impossible to
 * save from a line item.
 */
export const LINE_ITEM_UI_ONLY_TARGETING_KEYS = [
    "publishers_rules",
    "mandatory_tagid",
    "targeting_geolist",
    "conversion_geolist",
    "audienceSelector",
    "use_targeting_users_list_for_pretargeting",
    "dooh_campaign",
    "dmp_audiences_or",
    "dmp_audiences_blacklist",
] as const;

/** Removes the UI-only keys from `targeting` in place. */
export function stripLineItemUiOnlyTargetingKeys(targeting: Record<string, unknown>): void {
    LINE_ITEM_UI_ONLY_TARGETING_KEYS.forEach((key) => delete targeting[key]);
}

type TargetingLike = { [key: string]: unknown } | null | undefined;

const enabledPositions = (value: unknown): Set<string> =>
    new Set(
        value && typeof value === "object"
            ? Object.entries(value as Record<string, unknown>)
                  .filter(([, on]) => on === true)
                  .map(([key]) => key)
            : []
    );

/**
 * A 2xx from the DSP does not prove it applied the video options: an ignored value comes back
 * echoed as it was, and the save toast used to announce success anyway.
 *
 * Only meaningful where the DSP echoes the targeting in full, which is the campaign PUT. A line item
 * PUT comes back with `targeting` truncated (no video_positions even when it was applied), so an
 * absent key there says nothing about whether it took effect.
 *
 * Only what this save actually sent is compared (the diff can carry just some sub-keys of
 * `video_positions`), and an empty value on either side means "not set". Returns the first field
 * that came back different, or null.
 */
export function findRejectedVideoOption(sent: TargetingLike, received: TargetingLike): "video_positions" | "rewarded" | null {
    if (!sent) {
        return null;
    }

    if (sent["video_positions"] !== undefined) {
        const sentPositions = sent["video_positions"];
        const receivedOn = enabledPositions(received?.["video_positions"]);
        const rejected =
            sentPositions && typeof sentPositions === "object"
                ? Object.entries(sentPositions as Record<string, unknown>).some(([key, on]) => (on === true) !== receivedOn.has(key))
                : receivedOn.size > 0;

        if (rejected) {
            return "video_positions";
        }
    }

    if (sent["rewarded"] !== undefined && Boolean(sent["rewarded"]) !== Boolean(received?.["rewarded"])) {
        return "rewarded";
    }

    return null;
}

/**
 * The diff a save sends can hold only the sub-keys of `video_positions` that changed, e.g.
 * `{ "mid-roll": true }`. The DSP replaces the object, so that drops every other position: on a line
 * item it wiped the Pre-roll inherited from the campaign, leaving Mid-roll as the only placement.
 * Whenever the save carries `video_positions`, send the whole current value instead. A clear
 * (`null`) is left as it is.
 */
export function completeVideoPositions(targeting: TargetingLike, current: unknown): void {
    if (!targeting) {
        return;
    }

    const sent = targeting["video_positions"];
    if (sent && typeof sent === "object" && current && typeof current === "object") {
        targeting["video_positions"] = { ...(current as Record<string, unknown>) };
    }
}
